'use strict';

/**
 * Utility function to determine the type of a given input.
 *
 * @param {*} input - The input variable to check.
 * @returns {string} A string describing the type of the input: 'null', 'undefined', 'object', 'array', or the type as returned by typeof.
 */
function getTypeOf(input) {
  if (input === null) return 'null';
  if (typeof input === 'undefined') return 'undefined';
  return typeof input === 'object' ? (Array.isArray(input) ? 'array' : 'object') : typeof input;
}

/**
 * Clones a value based on its type. Supports deep cloning for objects and arrays.
 *
 * @param {*} value - The value to clone.
 * @returns {*} A deep cloned copy of the input value.
 */
function cloneValue(value) {
  const type = getTypeOf(value);
  return type === 'object' ? quickCloneObject(value) : type === 'array' ? quickCloneArray(value) : value;
}

/**
 * Clones an array by creating a new array with cloned values.
 *
 * @param {Array} input - The array to clone.
 * @returns {Array} A new array with cloned values.
 */
function quickCloneArray(input) {
  return input.map(cloneValue);
}

/**
 * Clones an object by creating a new object with cloned properties.
 *
 * @param {Object} input - The object to clone.
 * @returns {Object} A new object with cloned properties.
 */
function quickCloneObject(input) {
  return Object.keys(input).reduce((output, key) => {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      output[key] = cloneValue(input[key]);
    }
    return output;
  }, {});
}

/**
 * Recursively merges properties from source objects into a target object.
 *
 * @param {Object} target - The target object to merge properties into.
 * @param {Array} [_objects] - The source objects to merge.
 * @param {Object} [_options] - Options to customize the merge behavior.
 * @returns {Object} The merged object with deep cloning applied.
 */
function executeDeepMerge(target, _objects = [], _options = {}) {
  const options = {
    arrayBehaviour: _options.arrayBehaviour || 'replace',
  };
  const objects = _objects.map((object) => object || {});
  const output = target || {};

  for (const object of objects) {
    for (const key of Object.keys(object)) {
      const value = object[key];
      const type = getTypeOf(value);
      const existingValueType = getTypeOf(output[key]);

      if (type === 'object') {
        output[key] =
          existingValueType !== 'undefined'
            ? executeDeepMerge({}, [output[key] || {}, quickCloneObject(value)], options)
            : quickCloneObject(value);
      } else if (type === 'array') {
        output[key] =
          existingValueType === 'array'
            ? options.arrayBehaviour === 'merge'
              ? output[key].concat(quickCloneArray(value))
              : quickCloneArray(value)
            : quickCloneArray(value);
      } else {
        output[key] = value;
      }
    }
  }

  return output;
}

/**
 * Merges multiple source objects into a target object with deep cloning of nested properties.
 * The target object is not mutated.
 *
 * @param {Object} target - The target object to merge properties into.
 * @param {...Object} objects - The source objects to merge.
 * @returns {Object} The merged object.
 */
export function objectAssignDeep(target, ...objects) {
  return executeDeepMerge(target, objects);
}

/**
 * Merges multiple source objects into a new object with deep cloning of nested properties.
 * The target object is not mutated.
 *
 * @param {...Object} objects - The source objects to merge.
 * @returns {Object} The new merged object.
 */
export function objectAssignDeepInto(...objects) {
  return executeDeepMerge({}, objects);
}

/**
 * Merges multiple source objects into a target object with customizable behavior through options.
 * The target object is not mutated.
 *
 * @param {Object} target - The target object to merge properties into.
 * @param {Array} objects - The source objects to merge.
 * @param {Object} options - Options to customize the merge behavior.
 * @returns {Object} The merged object.
 */
export function objectAssignDeepWithOptions(target, objects, options) {
  return executeDeepMerge(target, objects, options);
}
